'use client';

import { useProgramNoun } from '@/lib/program-noun';
import type { ReactNode } from 'react';

import { useMemo, useState, useSyncExternalStore } from 'react';
import MiniSearch from 'minisearch';
import Image from 'next/image';
import { SmartLink } from '@/components/smart-link';
import { EmptyState } from '@/components/ui/empty-state';
import { CARD_SEARCH_OPTIONS, rankCards, type CardDoc } from '@/lib/search';
import { Combobox } from '@/components/ui/combobox';
import type { FacetDefinition, ProgramFacetValues } from '@/lib/program-facets';

/**
 * Cards sit three across inside the page container, so on wide screens a card
 * is about 360px, not a third of the viewport. Saying 33vw made the browser
 * fetch 1080px files for a 360px card on retina laptops (about 1.5x too big).
 */
const PROGRAM_CARD_SIZES = '(min-width: 1200px) 360px, (min-width: 1024px) 31vw, (min-width: 640px) 50vw, 100vw';

export interface ProgramCard {
  slug: string;
  title: string;
  heroImage?: string;
  excerpt?: string;
  isVirtual: boolean;
  /** Which filter options this card matches (see lib/program-facets). */
  facets?: ProgramFacetValues;
}

type Picks = Partial<Record<FacetDefinition['key'], string>>;

/** Desktop columns for search + N filters, as static classes Tailwind can see. */
const FILTER_COLUMNS: Readonly<Record<number, string>> = {
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
  5: 'lg:grid-cols-5',
};

/**
 * The filter picks live in the URL (`?type=…&size=…`) so an ad or a shared link
 * can open a filtered list, and the URL is the only copy of them: the grid
 * reads `location.search` through useSyncExternalStore and writing a pick
 * replaces the query and notifies readers.
 */
const PICKS_EVENT = 'program-filter-change';
const subscribe = (notify: () => void) => {
  window.addEventListener(PICKS_EVENT, notify);
  window.addEventListener('popstate', notify);
  return () => {
    window.removeEventListener(PICKS_EVENT, notify);
    window.removeEventListener('popstate', notify);
  };
};
const readSearch = () => window.location.search;
const readServerSearch = () => '';

function picksFromSearch(search: string, filters: readonly FacetDefinition[]): Picks {
  const params = new URLSearchParams(search);
  const picks: Picks = {};
  for (const f of filters) {
    const v = params.get(f.key);
    if (v && f.options.includes(v)) picks[f.key] = v;
  }
  return picks;
}

const TABS = ['All', 'In-Person', 'Virtual'] as const;
type Tab = (typeof TABS)[number];

/** Searchable, In-Person/Virtual-filterable program card grid. */
export function ProgramGrid({
  programs,
  emptyTone = 'mist',
  showTypeFilter = true,
  lead,
  leadSpan = 'two',
  hrefBase = '/programs',
  ctaLabel = 'See the activity',
  clampExcerpt = false,
  filters = [],
}: {
  programs: ProgramCard[];
  emptyTone?: 'mist' | 'white';
  /** Off for lists that are already one type (e.g. /virtual-programs). */
  showTypeFilter?: boolean;
  /**
   * A feature card (the category video, say) that takes the first two grid
   * slots with the same card chrome as the programs. Hidden while a search
   * or type filter is active, so results always start at the first slot.
   */
  lead?: ReactNode;
  /** `two` takes the first two slots; `full` spans the row (short lists, horizontal card). */
  leadSpan?: 'two' | 'full';
  /** Where cards link: `${hrefBase}/${slug}`. The program pages by default; a partner showcase links inside itself. */
  hrefBase?: string;
  /** The card's call to action. */
  ctaLabel?: string;
  /** Cap card copy at four lines so a row of cards lines up (off by default). */
  clampExcerpt?: boolean;
  /**
   * Dropdown filters above the grid (type, group size, …). Each card lists the
   * options it matches in `facets`. Picks are mirrored to the URL query.
   */
  filters?: readonly FacetDefinition[];
}) {
  const noun = useProgramNoun();
  const [tab, setTab] = useState<Tab>('All');
  const [query, setQuery] = useState('');
  const search = useSyncExternalStore(subscribe, readSearch, readServerSearch);
  const picks = useMemo(() => picksFromSearch(search, filters), [search, filters]);
  const q = query.trim();
  const activePicks = filters.filter((f) => picks[f.key]);

  const writePicks = (next: Picks) => {
    const params = new URLSearchParams(window.location.search);
    for (const f of filters) {
      const v = next[f.key];
      if (v) params.set(f.key, v);
      else params.delete(f.key);
    }
    const qs = params.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`);
    window.dispatchEvent(new Event(PICKS_EVENT));
  };
  const pick = (key: FacetDefinition['key'], value: string) => writePicks({ ...picks, [key]: value || undefined });
  const clearAll = () => {
    writePicks({});
    setQuery('');
  };

  /**
   * The same fuzzy, ranked matching as the site search rather than a
   * substring test on the title — a title-only `includes` cannot get from
   * "build bikes" to "Bike Build Donation®", which is exactly what people
   * type. Built over the cards on screen, so it costs no extra fetch.
   */
  const engine = useMemo(() => {
    const search = new MiniSearch<CardDoc>(CARD_SEARCH_OPTIONS);
    search.addAll(
      programs.map((program) => ({
        id: program.slug,
        title: program.title,
        excerpt: program.excerpt ?? '',
      })),
    );
    return search;
  }, [programs]);

  /** slug -> relevance position, so the grid can order by best match. */
  const ranking = useMemo(() => rankCards(engine, q), [engine, q]);

  const mixedTypes =
    showTypeFilter && programs.some((p) => p.isVirtual) && programs.some((p) => !p.isVirtual);
  const searchable = programs.length > 6;
  const visible = programs
    .filter((program) => {
      if (tab === 'In-Person' && program.isVirtual) return false;
      if (tab === 'Virtual' && !program.isVirtual) return false;
      if (ranking && !ranking.has(program.slug)) return false;
      for (const f of activePicks) {
        const want = picks[f.key];
        if (want && !program.facets?.[f.key]?.includes(want)) return false;
      }
      return true;
    })
    .sort((a, b) =>
      ranking ? (ranking.get(a.slug) ?? 0) - (ranking.get(b.slug) ?? 0) : 0,
    );

  const filtering = activePicks.length > 0;

  return (
    <div>
      {filters.length > 0 ? (
        <div className="rounded-2xl border border-brand-navy/10 bg-white p-4 shadow-sm sm:p-5">
          <div className={`grid gap-3 sm:grid-cols-2 ${FILTER_COLUMNS[filters.length + 1] ?? 'lg:grid-cols-5'}`}>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="program-filter-search" className="text-xs font-bold tracking-wide text-brand-navy/70 uppercase">
                Search
              </label>
              <input
                id="program-filter-search"
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={`Search ${noun.many}…`}
                className="w-full rounded-lg border border-brand-navy/15 bg-white px-4 py-2.5 text-sm text-brand-navy placeholder:text-brand-navy/70 outline-none focus:border-brand-blue focus:ring-4 focus:ring-brand-blue/10"
              />
            </div>
            {filters.map((f) => (
              <div key={f.key} className="flex flex-col gap-1.5">
                <label htmlFor={`program-filter-${f.key}`} className="text-xs font-bold tracking-wide text-brand-navy/70 uppercase">
                  {f.label}
                </label>
                <Combobox
                  id={`program-filter-${f.key}`}
                  name={`program-filter-${f.key}`}
                  groups={[{ group: f.label, options: f.options }]}
                  allowEmpty
                  emptyLabel={f.anyLabel}
                  searchable={false}
                  placeholder={f.anyLabel}
                  value={picks[f.key] ?? ''}
                  onValueChange={(value) => pick(f.key, value)}
                />
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-4 text-sm">
            <span className="font-semibold text-brand-navy" aria-live="polite">
              {visible.length} of {programs.length} {noun.many}
            </span>
            {filtering || q ? (
              <button type="button" onClick={clearAll} className="font-semibold text-brand-blue hover:underline">
                Clear filters
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
      {filters.length === 0 && (searchable || mixedTypes) ? (
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        {searchable ? (
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={`Search ${noun.many}…`}
          aria-label={`Search ${noun.many}`}
          className="w-full rounded-full border border-brand-navy/15 sm:max-w-xs bg-white px-4 py-2 text-sm text-brand-navy placeholder:text-brand-navy/70 transition-shadow outline-none focus:border-brand-blue focus:ring-4 focus:ring-brand-blue/10"
        />
        ) : null}
        {searchable ? (
        <span className="text-sm text-brand-navy/70">
          {visible.length} of {programs.length}
        </span>
        ) : null}
        {mixedTypes ? (
        <div className="flex w-full justify-between rounded-full border border-brand-navy/15 bg-white p-1 sm:ml-auto sm:w-auto sm:justify-start">
          {TABS.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`flex-1 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors sm:flex-none ${
                tab === t ? 'bg-brand-royal text-white' : 'text-brand-navy/75 hover:text-brand-royal'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
        ) : null}
      </div>
      ) : null}

      <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {lead && !q && !filtering && tab === 'All' ? (
          <div
            className={`overflow-hidden rounded-2xl border border-brand-navy/5 bg-white shadow-sm sm:col-span-2 ${
              leadSpan === 'full' ? 'lg:col-span-3' : ''
            }`}
          >
            {lead}
          </div>
        ) : null}
        {visible.map((program) => (
          <SmartLink
            key={program.slug}
            href={`${hrefBase}/${program.slug}`}
            className="group flex flex-col overflow-hidden rounded-2xl border border-brand-navy/5 bg-white shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl"
          >
            {program.heroImage ? (
              <Image
                src={program.heroImage}
                width={560}
                height={320}
                alt={program.title}
                sizes={PROGRAM_CARD_SIZES}
                className="aspect-[10/7] w-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
            ) : (
              <div className="aspect-[10/7] w-full bg-brand-navy/5" />
            )}
            <div className="flex flex-1 flex-col p-5">
              <h2 className="text-lg font-bold text-brand-navy group-hover:text-brand-blue">
                {program.title}
              </h2>
              {program.excerpt ? (
                <p className={`mt-2 flex-1 text-sm leading-relaxed text-brand-navy/75 ${clampExcerpt ? 'line-clamp-4' : ''}`}>
                  {program.excerpt}
                </p>
              ) : null}
              <span className="mt-4 inline-block text-sm font-semibold text-brand-blue">
                {ctaLabel} →
              </span>
            </div>
          </SmartLink>
        ))}
      </div>
      {visible.length === 0 ? (
        <EmptyState
          tone={emptyTone}
          heading={filtering ? 'No programs match those filters' : 'No programs match that search'}
          body={
            query
              ? `We couldn't find a program for “${query}” here. Try a different term, or browse every program.`
              : 'Try a different filter, or browse every program.'
          }
        >
          {query || filtering ? (
            <button
              type="button"
              onClick={clearAll}
              className="rounded-full border-2 border-brand-royal px-6 py-2.5 text-sm font-bold text-brand-royal transition-colors hover:bg-brand-royal hover:text-white"
            >
              {filtering ? 'Clear filters' : 'Clear search'}
            </button>
          ) : null}
          <SmartLink
            href="/program-categories/all-programs"
            className="rounded-full bg-brand-yellow px-6 py-2.5 text-sm font-bold text-brand-navy transition-all hover:-translate-y-px hover:bg-brand-yellow-dark"
          >
            Browse all programs
          </SmartLink>
        </EmptyState>
      ) : null}
    </div>
  );
}
